import React, { useState } from 'react';
import jsPDF from 'jspdf';
import { 
  X, 
  Mail, 
  ArrowRight, 
  ShieldCheck, 
  Sparkles, 
  CheckCircle2, 
  Download, 
  Building2, 
  TrendingUp, 
  Copy,
  Check,
  Lock,
  FileText,
  Printer
} from 'lucide-react';

export interface MarginDiagnosticData {
  currencySym: string;
  revenue: number;
  expense: number;
  margin: number;
  taxBuffer: number;
  vatAmount?: number;
  incomeTaxBuffer?: number;
  spendable: number;
  jurisdiction: string;
  jurisdictionLabel: string;
  healthInsight: string;
}

interface MarginDiagnosticEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: MarginDiagnosticData;
  onContinueToTrial: (email: string) => void;
  showToast?: (msg: string, type?: 'success' | 'info') => void;
}

export function MarginDiagnosticEmailModal({
  isOpen,
  onClose,
  data,
  onContinueToTrial,
  showToast
}: MarginDiagnosticEmailModalProps) {
  const [email, setEmail] = useState('');
  const [founderName, setFounderName] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const generateReportText = () => {
    return `===================================================================
AZURAOS // 1-PAGE MARGIN DEFENSE & STATUTORY TAX AUDIT
Generated: ${new Date().toLocaleDateString()} | Jurisdiction: ${data.jurisdictionLabel}
===================================================================

FOUNDER / COMPANY: ${founderName.trim() || 'Independent Operator'}
REGISTERED CONTACT: ${email.trim() || 'Direct Download'}

1. OPERATIONAL FINANCIAL REALITY
-------------------------------------------------------------------
Gross Monthly Billings / Inflow:    ${data.currencySym}${data.revenue.toLocaleString()}
Monthly Operating Overhead & Burn:  -${data.currencySym}${data.expense.toLocaleString()}
Operating Profit Margin %:          ${data.margin.toFixed(1)}%

2. STATUTORY TAX RESERVES & DISTRIBUTABLE CASH
-------------------------------------------------------------------
Statutory Tax Buffer:               -${data.currencySym}${data.taxBuffer.toLocaleString()} (${data.jurisdictionLabel})
${data.vatAmount ? `  • Indirect Tax (VAT/Sales Tax):   -${data.currencySym}${data.vatAmount.toLocaleString()}\n  • Provisional Corporate Hold:     -${data.currencySym}${(data.incomeTaxBuffer || 0).toLocaleString()}\n` : ''}Indicative Distributable Cash:      ${data.currencySym}${data.spendable.toLocaleString()} (Safe Owner Draw)

3. STRATEGIC MARGIN HEALTH INSIGHT
-------------------------------------------------------------------
${data.healthInsight}

===================================================================
SECURITY & INFRASTRUCTURE GUARANTEE
Dual-Vault Client Architecture • Client-Side AES-256-GCM Encryption
Air-Gapped Bank Statement Parsing • Zero Bank Password Sharing (No Plaid)
Zero AI Model Training Guarantee • Powered by Azura Seraph™ (Pty) Ltd
Google Cloud Partner Network Member • https://azuraos.app
===================================================================`;
  };

  const handleDownloadPdf = () => {
    setIsGeneratingPdf(true);
    try {
      const doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
      });

      // Background canvas
      doc.setFillColor(11, 15, 17);
      doc.rect(0, 0, 210, 297, 'F');

      // Top Header Teal Rule
      doc.setDrawColor(20, 184, 166);
      doc.setLineWidth(1);
      doc.line(15, 26, 195, 26);

      // Title & Branding
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(255, 255, 255);
      doc.text('AzuraOS — 1-Page Margin & Statutory Tax Audit', 15, 20);

      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(20, 184, 166);
      doc.text(`CONFIDENTIAL FOUNDER DIAGNOSTIC • JURISDICTION: ${data.jurisdictionLabel.toUpperCase()}`, 15, 33);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(156, 163, 175);
      doc.text(`Generated: ${new Date().toLocaleDateString()} | Recipient: ${email.trim() || 'Direct Download'} | Org: ${founderName.trim() || 'Independent Operator'}`, 15, 39);

      // Section 1: Financial Health Reality
      doc.setFillColor(18, 22, 24);
      doc.roundedRect(15, 46, 180, 50, 3, 3, 'F');
      doc.setDrawColor(32, 40, 43);
      doc.roundedRect(15, 46, 180, 50, 3, 3, 'D');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text('1. Operational Financial Reality', 20, 56);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(156, 163, 175);
      doc.text('Gross Monthly Billings / Inflows:', 20, 66);
      doc.text('Monthly Operating Overhead & Burn:', 20, 75);
      doc.text('Operating Profit Margin:', 20, 84);

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text(`${data.currencySym}${data.revenue.toLocaleString()}`, 140, 66);
      doc.setTextColor(244, 63, 94);
      doc.text(`-${data.currencySym}${data.expense.toLocaleString()}`, 140, 75);
      doc.setTextColor(52, 211, 153);
      doc.text(`${data.margin.toFixed(1)}%`, 140, 84);

      // Section 2: Statutory Tax Reserve & Distributable Cash
      doc.setFillColor(18, 22, 24);
      doc.roundedRect(15, 103, 180, 58, 3, 3, 'F');
      doc.setDrawColor(32, 40, 43);
      doc.roundedRect(15, 103, 180, 58, 3, 3, 'D');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text('2. Statutory Tax Buffer & Distributable Cash', 20, 113);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(156, 163, 175);
      doc.text(`Statutory Tax Buffer (${data.jurisdictionLabel}):`, 20, 122);
      if (data.vatAmount) {
        doc.text(`  • Indirect Tax (VAT/Sales Tax Estimate):`, 20, 130);
        doc.text(`  • Provisional Corporate / Income Hold:`, 20, 137);
      }
      doc.text('Indicative Distributable Cash (Safe Draw):', 20, 149);

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(251, 191, 36);
      doc.text(`-${data.currencySym}${data.taxBuffer.toLocaleString()}`, 140, 122);
      if (data.vatAmount) {
        doc.text(`-${data.currencySym}${data.vatAmount.toLocaleString()}`, 140, 130);
        doc.text(`-${data.currencySym}${(data.incomeTaxBuffer || 0).toLocaleString()}`, 140, 137);
      }
      doc.setTextColor(20, 184, 166);
      doc.setFontSize(11);
      doc.text(`${data.currencySym}${data.spendable.toLocaleString()}`, 140, 149);

      // Section 3: Strategic Health Insight
      doc.setFillColor(18, 22, 24);
      doc.roundedRect(15, 168, 180, 45, 3, 3, 'F');
      doc.setDrawColor(32, 40, 43);
      doc.roundedRect(15, 168, 180, 45, 3, 3, 'D');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(255, 255, 255);
      doc.text('3. Strategic Margin Health Diagnostic', 20, 178);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(209, 213, 219);
      const splitInsight = doc.splitTextToSize(data.healthInsight, 170);
      doc.text(splitInsight, 20, 188);

      // Section 4: Security & Guarantees Footer
      doc.setFontSize(8);
      doc.setTextColor(107, 114, 128);
      doc.text('Security Guarantees: Dual-Vault Client Architecture • Client-Side AES-256 Encryption • Air-Gapped Banking • Zero AI Training', 15, 230);
      doc.text('Legal Note: This diagnostic provides indicative financial planning models. Formal dividend declarations and tax submissions depend on company registration and SARS/IRS filing.', 15, 237);
      doc.text('AzuraOS is a proprietary sovereign product of Azura Seraph (Pty) Ltd (Reg: 2024/560516/07), South Africa • https://azuraos.app', 15, 244);

      doc.save(`AzuraOS-Margin-Audit-${data.currencySym}${data.revenue}.pdf`);
      if (showToast) showToast('✓ 1-Page PDF Margin Audit generated & downloaded!', 'success');
    } catch (err) {
      console.error('PDF export error:', err);
      if (showToast) showToast('PDF generation fallback to text summary.', 'info');
      triggerFileDownload();
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const triggerFileDownload = () => {
    setIsDownloading(true);
    try {
      const reportContent = generateReportText();
      const blob = new Blob([reportContent], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `AzuraOS-Margin-Audit-${data.currencySym}${data.revenue}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      if (showToast) showToast('✓ Text summary downloaded (.txt)', 'success');
    } catch (err) {
      console.error('Report download error:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      if (showToast) showToast('Please enter a valid work email address', 'info');
      return;
    }

    // Save lead locally to persistent registry for founder follow-up & tracking
    try {
      const existingLeads = JSON.parse(localStorage.getItem('azuraos_margin_leads') || '[]');
      existingLeads.push({
        email: email.trim(),
        name: founderName.trim(),
        data,
        timestamp: new Date().toISOString()
      });
      localStorage.setItem('azuraos_margin_leads', JSON.stringify(existingLeads));
    } catch (err) {
      console.warn('Failed to cache lead locally:', err);
    }

    // Trigger instant PDF download directly to user device
    handleDownloadPdf();

    setIsSubmitted(true);
  };

  const handleCopyReport = () => {
    try {
      navigator.clipboard.writeText(generateReportText());
      setCopied(true);
      if (showToast) showToast('Audit summary copied to clipboard!', 'success');
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.warn('Clipboard copy error:', err);
    }
  };

  const handleProceedToTrial = () => {
    onClose();
    onContinueToTrial(email.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div 
        className="w-full max-w-lg bg-[#111416] border border-[#14B8A6]/40 rounded-2xl shadow-2xl overflow-hidden relative flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1F2528] bg-[#0E1113]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#14B8A6]/10 border border-[#14B8A6]/30 flex items-center justify-center text-[#14B8A6]">
              <TrendingUp size={16} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white leading-tight">
                {isSubmitted ? 'Diagnostic Ready & Downloaded' : 'Download Your 1-Page Margin Audit'}
              </h3>
              <span className="text-[10px] text-teal-400 font-mono block">
                {data.currencySym}{data.revenue.toLocaleString()} Monthly Inflow • {data.margin.toFixed(1)}% Real Margin
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-[#1E2528] transition-colors cursor-pointer"
            aria-label="Close modal"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-5 text-xs">
          
          {/* Diagnostic Snapshot Card */}
          <div className="p-4 bg-[#090C0D] border border-[#1F2528] rounded-xl space-y-3 font-mono">
            <div className="flex items-center justify-between text-[11px] text-gray-400 border-b border-[#1A2225] pb-2">
              <span>Metric</span>
              <span>Calculated Amount</span>
            </div>

            <div className="flex justify-between items-center text-gray-300">
              <span className="font-sans text-gray-400">Gross Monthly Billings / Inflows:</span>
              <strong className="text-white text-xs">{data.currencySym}{data.revenue.toLocaleString()}</strong>
            </div>

            <div className="flex justify-between items-center text-gray-300">
              <span className="font-sans text-gray-400">Monthly Operating Overhead:</span>
              <span className="text-rose-300">-{data.currencySym}{data.expense.toLocaleString()}</span>
            </div>

            <div className="flex justify-between items-center text-gray-300">
              <span className="font-sans text-gray-400">Operating Profit Margin:</span>
              <strong className={`font-bold ${data.margin >= 55 ? 'text-emerald-400' : data.margin >= 40 ? 'text-[#14B8A6]' : 'text-rose-400'}`}>
                {data.margin.toFixed(1)}%
              </strong>
            </div>

            <div className="flex justify-between items-center text-amber-300/90 pt-1 border-t border-[#1A2225]">
              <span className="font-sans text-gray-400">Statutory Tax Buffer ({data.jurisdictionLabel}):</span>
              <strong className="text-amber-300">-{data.currencySym}{data.taxBuffer.toLocaleString()}</strong>
            </div>

            <div className="flex justify-between items-center text-emerald-400 font-bold pt-1 border-t border-[#1A2225]">
              <span className="font-sans text-emerald-300">Indicative Distributable Cash:</span>
              <span className="text-sm">{data.currencySym}{data.spendable.toLocaleString()}</span>
            </div>
          </div>

          {/* Strategic Insight Box */}
          <div className="p-3.5 bg-[#14B8A6]/5 border border-[#14B8A6]/30 rounded-xl space-y-1.5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-teal-400 font-mono flex items-center gap-1.5">
              <Sparkles size={12} />
              <span>Operational Margin Insight</span>
            </div>
            <p className="text-gray-300 text-[11px] leading-relaxed font-sans">
              {data.healthInsight}
            </p>
          </div>

          {!isSubmitted ? (
            /* Lead Capture Form */
            <form onSubmit={handleSubmit} className="space-y-4 pt-1">
              <div className="space-y-2">
                <label className="block text-gray-300 font-semibold text-xs">
                  Enter your work email to download your 1-Page Margin &amp; Tax Audit:
                </label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="founder@company.com"
                    className="w-full bg-[#090C0D] border border-[#252E32] focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] rounded-xl pl-10 pr-4 py-2.5 text-xs text-white placeholder-gray-500 outline-none transition"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-gray-400 text-[11px]">
                  Founder or Business Name (Optional)
                </label>
                <div className="relative">
                  <Building2 size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={founderName}
                    onChange={(e) => setFounderName(e.target.value)}
                    placeholder="Acme Studio or John Doe"
                    className="w-full bg-[#090C0D] border border-[#252E32] focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6] rounded-xl pl-10 pr-4 py-2.5 text-xs text-white placeholder-gray-500 outline-none transition"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isGeneratingPdf}
                className="w-full h-11 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold rounded-xl transition flex items-center justify-center gap-2 cursor-pointer shadow-md shadow-[#14B8A6]/20 active:scale-95 text-xs disabled:opacity-50"
              >
                <Download size={14} />
                <span>{isGeneratingPdf ? 'Generating PDF...' : 'Download 1-Page Formal PDF Audit (Instant)'}</span>
              </button>

              <p className="text-[10px] text-gray-500 text-center font-mono flex items-center justify-center gap-1.5">
                <Lock size={11} className="text-teal-400" />
                <span>Instant direct file download • Zero spam • 100% private data</span>
              </p>
            </form>
          ) : (
            /* Post-Submission Success & Activation Bridge */
            <div className="space-y-4 pt-1 animate-fade-in">
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center gap-2.5 text-emerald-300">
                <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
                <span className="text-xs">
                  Your 1-Page Margin Audit has been prepared for <strong>{email}</strong>!
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={isGeneratingPdf}
                  className="w-full py-2.5 px-2.5 bg-[#14B8A6]/15 hover:bg-[#14B8A6]/25 text-teal-300 hover:text-white border border-[#14B8A6]/40 rounded-xl font-bold flex items-center justify-center gap-1.5 cursor-pointer transition text-[11px]"
                >
                  <FileText size={13} className="text-teal-400" />
                  <span>Download PDF</span>
                </button>

                <button
                  type="button"
                  onClick={triggerFileDownload}
                  disabled={isDownloading}
                  className="w-full py-2.5 px-2.5 bg-[#1A2225] hover:bg-[#252F33] text-gray-200 hover:text-white border border-[#2C373C] rounded-xl font-semibold flex items-center justify-center gap-1.5 cursor-pointer transition text-[11px]"
                >
                  <Download size={13} className="text-gray-400" />
                  <span>Download .TXT</span>
                </button>

                <button
                  type="button"
                  onClick={handleCopyReport}
                  className="w-full py-2.5 px-2.5 bg-[#1A2225] hover:bg-[#252F33] text-gray-200 hover:text-white border border-[#2C373C] rounded-xl font-semibold flex items-center justify-center gap-1.5 cursor-pointer transition text-[11px]"
                >
                  {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} className="text-gray-400" />}
                  <span>{copied ? 'Copied!' : 'Copy Text'}</span>
                </button>
              </div>

              <div className="pt-2 border-t border-[#1F2528]">
                <button
                  type="button"
                  onClick={handleProceedToTrial}
                  className="w-full h-11 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold rounded-xl flex items-center justify-center gap-2 cursor-pointer transition shadow-md shadow-[#14B8A6]/20 active:scale-95 text-xs"
                >
                  <span>Launch AzuraOS Workspace (7-Day Free Trial)</span>
                  <ArrowRight size={14} />
                </button>
              </div>

              <p className="text-[10px] text-gray-400 text-center font-sans">
                Next: You will be routed to the <strong>Welcome Aboard</strong> briefing to set up your private workspace.
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
